import type { BusinessContext } from "../../../../types/businessContext";
import type {
  HealthScoreModel,
  HealthScoreSettings,
} from "../../../../services/healthScoreSettings/healthScoreSettingsEndpoints.interface";

export const HEALTH_SCORE_SCHEDULE_PRESETS = [
  {
    value: "weekly" as const,
    label: "Once a week (Monday at 9:00 AM)",
    cron: "0 9 * * 1",
  },
  {
    value: "monthly" as const,
    label: "Once a month (1st at 9:00 AM)",
    cron: "0 9 1 * *",
  },
  {
    value: "quarterly" as const,
    label: "Once a quarter (1st of Jan, Apr, Jul, Oct at 9:00 AM)",
    cron: "0 9 1 1,4,7,10 *",
  },
];

export const HEALTH_STATUS_OPTIONS = [
  { value: "healthy", label: "Healthy", className: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  { value: "at_risk", label: "At risk", className: "border-amber-200 bg-amber-50 text-amber-700" },
  { value: "critical", label: "Critical", className: "border-rose-200 bg-rose-50 text-rose-700" },
] as const;

export const HEALTH_SCORE_OUTPUT_SCHEMA_OBJECT = {
  type: "object",
  additionalProperties: false,
  properties: {
    account_id: { type: "string" },
    health_status: {
      type: "string",
      enum: ["healthy", "at_risk", "critical"],
    },
    summary: { type: "string" },
  },
  required: [
    "account_id",
    "health_status",
    "summary",
  ],
};

export const HEALTH_SCORE_OUTPUT_SCHEMA = JSON.stringify(HEALTH_SCORE_OUTPUT_SCHEMA_OBJECT, null, 2);

export const HEALTH_SCORE_OUTPUT_PREVIEW = `{
  "account_id": "acc_123",
  "health_status": "at_risk",
  "summary": "Usage has declined sharply and the account does not show broader adoption beyond one active champion."
}`;

export type HealthModelScopeDescriptor = Pick<
HealthScoreModel,
"scope_type" | "scope_key" | "scope_label" | "scope_value" | "scope_source_key" | "scope_source_label" | "match_field"
>;

export const GENERAL_HEALTH_MODEL_SCOPE_KEY = "general:default";

export const GENERAL_HEALTH_MODEL_SCOPE: HealthModelScopeDescriptor = {
  scope_type: "general",
  scope_key: GENERAL_HEALTH_MODEL_SCOPE_KEY,
  scope_label: "General",
  scope_value: "general",
  scope_source_key: "general",
  scope_source_label: "General",
  match_field: null,
};

export const buildHealthScoreSkillName = (userId: string) =>
  `ora_health_models_${userId.replace(/-/g, "_")}`;

export const buildHealthScoreAutomationName = (userId: string, scopeKey?: string) =>
  scopeKey
    ? `Ora health ${scopeKey} ${userId.slice(0, 8)}`
    : `Ora health model sync ${userId.slice(0, 8)}`;

export const getSchedulePresetValue = (runSchedule: string) =>
  HEALTH_SCORE_SCHEDULE_PRESETS.find((preset) => preset.cron === runSchedule)?.value || "weekly";

export const getHealthModelDefaultTitle = (scope: HealthModelScopeDescriptor) =>
  scope.scope_type === "general"
    ? "General Account Health Document"
    : scope.scope_type === "segment"
    ? `${scope.scope_label} Segment Health Document`
    : `${scope.scope_label} Relationship Health Document`;

export const getConfiguredHealthModels = (settings: HealthScoreSettings) =>
  settings.models.filter((model) => model.document_content.trim().length > 0);

const summarizeContextList = (items: Array<string | undefined | null>, fallback: string) => {
  const cleaned = items.map((item) => (item || "").trim()).filter(Boolean);
  if (!cleaned.length) return fallback;
  return cleaned.slice(0, 8).join(", ");
};

export const buildHealthModelGenerationPrompt = (
  scope: HealthModelScopeDescriptor,
  businessContext?: BusinessContext | null,
) => {
  const companyName = businessContext?.company_profile?.company_name?.trim() || "this workspace";
  const companyDescription = businessContext?.company_profile?.description?.trim() || "No company description provided.";
  const products = summarizeContextList(
    (businessContext?.products || []).map((product) => product.name),
    "No products defined.",
  );
  const features = summarizeContextList(
    (businessContext?.features || []).map((feature) => feature.name),
    "No features defined.",
  );
  const goals = summarizeContextList(
    (businessContext?.goals || []).map((goal) => goal.name),
    "No goals defined.",
  );
  const lifecycleStages = summarizeContextList(
    (businessContext?.lifecycle_stages || []).map((stage) => stage.name),
    "No lifecycle stages defined.",
  );
  const segments = summarizeContextList(
    (businessContext?.segments || []).map((segment) => segment.name),
    "No segments defined.",
  );

  // Look up segment description from business context if available
  const matchingSegment = scope.scope_type === "segment"
    ? (businessContext?.segments || []).find((s) => s.name?.trim() === scope.scope_value)
    : null;
  const segmentDesc = matchingSegment?.description?.trim();
  const segmentFilterSummary = matchingSegment?.fit_filter
    ? `\nThe segment's filter criteria: ${JSON.stringify(matchingSegment.fit_filter)}`
    : "";

  const scopeDescription = scope.scope_type === "general"
    ? `This document is the default account health definition for this workspace.
Use it only when an account does not match any segment-specific health model.`
    : scope.scope_type === "segment"
      ? `This document is for the "${scope.scope_label}" segment.${segmentDesc ? `\nSegment description: ${segmentDesc}` : ""}${segmentFilterSummary}
Tailor the health definition specifically to the characteristics and needs of accounts in this segment.`
      : `This document is for the "${scope.scope_label}" lifecycle stage.
The source category is "${scope.scope_source_label}" and the matching value is "${scope.scope_value}".`;

  return `Create a markdown health definition document for Hyperengage.

${scopeDescription}

Write the document as guidance that an AI account health analyst would follow when scoring one account at a time.

Requirements:
- Do not include a top-level title.
- Start with a short overview paragraph for this scope.
- Add 4 to 6 numbered sections that explain the most important health dimensions for this scope.
- In each section, include bold labels for Healthy Signs, At Risk Signs, and Critical Signs.
- Keep the writing specific and operational, not generic.
- Use normal markdown only. Do not mention APIs, JSON, tools, schemas, or implementation details.

Workspace context:
- Company: ${companyName}
- Company description: ${companyDescription}
- Products: ${products}
- Key features: ${features}
- Goals: ${goals}
- Lifecycle stages: ${lifecycleStages}
- Segments: ${segments}`;
};

const formatModelMatchRule = (model: HealthScoreModel) => {
  if (model.scope_type === "general") {
    return "Fallback only when no lifecycle-stage or segment health models match";
  }
  if (model.segment_filter?.predicates?.length) {
    return `Segment filter: ${model.scope_label} (multi-condition filter)`;
  }
  if (model.match_field?.trim()) {
    return `${model.match_field} = ${model.scope_value}`;
  }
  return `${model.scope_source_label} = ${model.scope_value}`;
};

export const buildHealthScoreSkillInstructions = (settings: HealthScoreSettings) => {
  const configuredModels = getConfiguredHealthModels(settings);
  const modelSummary = configuredModels
    .map((model) => `- ${model.scope_label}: ${formatModelMatchRule(model)}`)
    .join("\n");

  return `# Ora Health Models

Rules:
- The system will pass exactly one health model document for each run.
- Evaluate the account only for the provided health model.
- Specific lifecycle-stage and segment models can overlap, so the same account may be scored in multiple runs.
- The General model is fallback-only and is used only when no specific health models match.
- Choose exactly one final health_status value: healthy, at_risk, or critical.
- Keep the summary to one or two sentences grounded in evidence from the account and the provided health model.
- Keep the markdown explanation short. It should be a brief explainer for humans, not a long report.
- Return the structured JSON output exactly as requested by the schema.

Configured health models in settings:
${modelSummary || "- None configured yet."}`;
};

export const buildHealthScoreAutomationFilters = (settings: HealthScoreSettings) => {
  const configuredModels = getConfiguredHealthModels(settings);

  if (configuredModels.some((model) => model.scope_type === "general")) {
    return {};
  }

  // Field-based predicates from computed-label segments and lifecycle stages
  const fieldPredicates = configuredModels
    .filter((model) => Boolean(model.match_field?.trim()) && !model.segment_filter)
    .map((model) => ({
      entity_type: "accounts",
      field: model.match_field!,
      operator: "eq",
      value: model.scope_value,
      type: "string",
    }));

  // Full filter predicates from business-context segments
  const segmentFilterPredicates = configuredModels
    .filter((model) => model.segment_filter?.predicates?.length)
    .flatMap((model) => (model.segment_filter!.predicates as any[]));

  const allPredicates = [...fieldPredicates, ...segmentFilterPredicates];

  if (!allPredicates.length) {
    return {};
  }

  return {
    query_type: allPredicates.length > 1 ? "OR" : "AND",
    predicates: allPredicates,
  };
};

export const buildHealthScoreModelFilters = (model: HealthScoreModel): Record<string, any> => {
  if (model.scope_type === "general") {
    return {};
  }

  if (model.segment_filter?.predicates?.length) {
    return {
      query_type: model.segment_filter.predicates.length > 1 ? "OR" : "AND",
      predicates: model.segment_filter.predicates,
    };
  }

  if (model.match_field?.trim()) {
    return {
      query_type: "AND",
      predicates: [
        {
          entity_type: "accounts",
          field: model.match_field,
          operator: "eq",
          value: model.scope_value,
          type: "string",
        },
      ],
    };
  }

  return {};
};

export const buildHealthScoreSelectedFields = (
  defaultSelectedFields: string[],
  settings: HealthScoreSettings,
) => Array.from(new Set([
  ...defaultSelectedFields,
  ...getConfiguredHealthModels(settings)
    .map((model) => model.match_field?.trim())
    .filter((value): value is string => Boolean(value)),
]));
